import NotificationsIcon from "@mui/icons-material/Notifications";
import { IconButton, type SxProps, type Theme } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useNotificationsContext } from "../../../contexts/NotificationsContext";
import { useRecommendationsDrawerContext } from "../../../contexts/RecommendationsDrawerContext";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { countRequestsWithResponses } from "../../../utils/utilFunctions";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { BELL_BADGE_MAX, BellBadge } from "../../notificationBell/BellBadge";
import { bellBadgeSummary } from "./bellSummary";

type Props = {
	color: "inherit" | "primary" | "secondary" | "default";
	size: "small" | "medium" | "large";
	sx: SxProps<Theme>;
};

/**
 * The admin's header bell. Its badge is everything waiting for the admin: requests waiting for a decision — the same
 * count the staffing drawer's trigger shows (`countRequestsWithResponses` over `RequestsContext.staffRequests`) — plus
 * unseen notifications (the count the staff bell shows) — spelled out in its tooltip and accessible name ("3 requests
 * waiting · 1 unread notification"). A click opens the admin side panel (`UpdatesPanel`, mounted by `Layout`) on
 * Changes; its Requests and Notifications tabs carry their own share of the count.
 */
export const AdminUpdatesBell: FC<Props> = ({ color, size, sx }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const location = useLocation();
	const { chosenDepartment } = useChosenDepartment();
	const { staffRequests } = useRequestsContext();
	const { unseenNotificationCount } = useNotificationsContext();
	const { isOpen, openFromBell } = useRecommendationsDrawerContext();
	const requestsCount = useMemo(() => countRequestsWithResponses(staffRequests), [staffRequests]);
	const badgeCount = requestsCount + unseenNotificationCount;
	const summary = bellBadgeSummary(requestsCount, unseenNotificationCount, t);

	const handleOpen = () => {
		// Every open starts on the changes — the question the bell answers first.
		openFromBell();
		track("bell_panel_opened", {
			department_id: chosenDepartment.department_id,
			requests_count: requestsCount,
			unseen_notifications_count: unseenNotificationCount,
			referrer: location.pathname,
		});
	};

	return (
		<InHouseTooltip title={summary}>
			<IconButton
				data-testid="header-notification-bell"
				onClick={handleOpen}
				color={color}
				size={size}
				sx={sx}
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				aria-label={t("scheduleChanges.panel.bellAria", { summary })}
			>
				<BellBadge badgeContent={badgeCount} invisible={badgeCount === 0} max={BELL_BADGE_MAX}>
					<NotificationsIcon fontSize={size} />
				</BellBadge>
			</IconButton>
		</InHouseTooltip>
	);
};
